// src/components/lesson/ScenarioDragTemplate.jsx

import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  useNavigate,
  useParams,
} from "react-router-dom";

import LessonShell from "./LessonShell";
import { secureKnowledgeChecksEnabled, verifyKnowledgeCheck } from "../../lib/learning/secureKnowledgeChecks";
import KnowledgeCheckFeedback from "./KnowledgeCheckFeedback";

import {
  getKnowledgeCheckProgress,
  incrementKnowledgeCheckWrongAttempt,
  saveCorrectDragAnswer,
} from "../../lib/learning/knowledgeCheckProgress";

const TEMP_CHARACTER_IMAGE =
  "https://placehold.co/500x650/F3F3F3/999999?text=Replace+Character";

function shuffleWords(list = []) {
  const arr = [...list];

  for (let i = arr.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));

    [arr[i], arr[j]] = [arr[j], arr[i]];
  }

  return arr;
}

function normalizeText(value) {
  return String(value ?? "")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();
}

function normalizeCopy(value) {
  return String(value || "")
    .trim()
    .replace(/\s+/g, " ");
}

function removeRepeatedScenarioFromPrompt({ scenarioText, prompt }) {
  const scenario = normalizeCopy(scenarioText);
  const rawPrompt = normalizeCopy(prompt);

  if (!scenario || !rawPrompt) {
    return rawPrompt;
  }

  const scenarioLower = scenario.toLowerCase();
  const promptLower = rawPrompt.toLowerCase();

  if (!promptLower.startsWith(scenarioLower)) {
    return rawPrompt;
  }

  return rawPrompt
    .slice(scenario.length)
    .trim()
    .replace(/^[.:\-\s]+/, "")
    .trim();
}

function idsEqual(a, b) {
  return String(a) === String(b);
}

export default function ScenarioDragTemplate({
  questionKey,
  secureAssessment,

  title = "",
  prompt = "",

  words = [],
  definitions = [],

  correctFeedbackText = "",
  incorrectFeedbackText = "",
  incorrectSupportText = "",

  characterImageSrc = TEMP_CHARACTER_IMAGE,
  characterImageAlt = "Scenario character",

  scenarioText = "",

  onNext,
  onIncorrectRetry,

  nextLabel = "Next",

  backTo,
  backLabel = "Back",
  onBack,

  validatePair = (word, definition) =>
    idsEqual(word?.id, definition?.id),

  showHelper = true,
  helperMain = "Tap an answer, then tap the matching box.",
  helperTip = "",

  conceptNumber = 1,
  conceptTotal = 1,

  progressStep = 1,
  progressTotal = 11,

  conceptIcon = "💡",
  submitLabel,
}) {
  const navigate = useNavigate();

  const {
    questId,
    subquestId,
    levelKey,
  } = useParams();

  const storageContext = useMemo(
    () => ({
      questId,
      subquestId,
      levelKey,
      questionKey,
    }),
    [
      questId,
      subquestId,
      levelKey,
      questionKey,
    ]
  );

  const [matches, setMatches] = useState([]);
  const [selectedWord, setSelectedWord] = useState(null);
  const [feedbackState, setFeedbackState] = useState("answering");
  const [answerLocked, setAnswerLocked] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verifyError, setVerifyError] = useState("");
  const secureMode = secureKnowledgeChecksEnabled && !!secureAssessment;
  const [shuffledWords, setShuffledWords] = useState(() =>
    shuffleWords(words)
  );
  const [isTouchDevice, setIsTouchDevice] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const coarse = window.matchMedia?.("(pointer: coarse)")?.matches;

    setIsTouchDevice(
      !!coarse ||
        "ontouchstart" in window
    );
  }, []);

  const getWordById = useCallback(
    (wordId) =>
      words.find((word) =>
        idsEqual(word?.id, wordId)
      ),
    [words]
  );

  const getDefinitionById = useCallback(
    (definitionId) =>
      definitions.find((definition) =>
        idsEqual(definition?.id, definitionId)
      ),
    [definitions]
  );

  const baseValidatePair = useCallback(
    (word, definition) => {
      if (!word || !definition) {
        return false;
      }

      if (validatePair(word, definition)) {
        return true;
      }

      if (
        word?.matchKey &&
        definition?.matchKey &&
        String(word.matchKey) === String(definition.matchKey)
      ) {
        return true;
      }

      return false;
    },
    [validatePair]
  );

  /*
   * Preserve duplicate-label support.
   */
  const effectiveValidatePair = useCallback(
    (word, definition) => {
      if (!word || !definition) {
        return false;
      }

      if (baseValidatePair(word, definition)) {
        return true;
      }

      const text = normalizeText(word.text);

      if (!text) {
        return false;
      }

      const identical = words.filter(
        (candidate) =>
          normalizeText(candidate?.text) === text
      );

      if (identical.length > 1) {
        return identical.some((candidate) =>
          baseValidatePair(candidate, definition)
        );
      }

      return false;
    },
    [
      baseValidatePair,
      words,
    ]
  );

  /*
   * Restore previously completed answer.
   *
   * On a fresh redo, SanityFlowRunner clears the local storage
   * for this level before the user reaches this question again.
   */
  useEffect(() => {
    const saved = secureMode ? null : getKnowledgeCheckProgress(storageContext);

    setFeedbackState("answering");
    setSelectedWord(null);

    if (
      saved?.completedCorrectly &&
      Array.isArray(saved?.correctMatches) &&
      saved.correctMatches.length > 0
    ) {
      const validMatches = saved.correctMatches.filter(
        (match) =>
          !!getWordById(match?.wordId) &&
          !!getDefinitionById(match?.defId)
      );

      if (
        definitions.length > 0 &&
        validMatches.length === definitions.length
      ) {
        setMatches(validMatches);
        setAnswerLocked(true);
        return;
      }
    }

    setMatches([]);
    setAnswerLocked(false);
    setShuffledWords(shuffleWords(words));
  }, [
    storageContext,
    secureMode,
    words,
    definitions,
    getWordById,
    getDefinitionById,
  ]);

  const isWordMatched = (wordId) =>
    matches.some((match) =>
      idsEqual(match?.wordId, wordId)
    );

  const getMatchForDefinition = (definitionId) =>
    matches.find((match) =>
      idsEqual(match?.defId, definitionId)
    );

  const toggleSelectedWord = (word) => {
    if (answerLocked || !word) {
      return;
    }

    setSelectedWord((current) =>
      idsEqual(current?.id, word.id) ? null : word
    );
  };

  function placeWordIntoDefinition(wordId, definitionId) {
    if (
      answerLocked ||
      wordId == null ||
      definitionId == null
    ) {
      return;
    }

    const withoutWord = matches.filter(
      (match) => !idsEqual(match.wordId, wordId)
    );

    const withoutTarget = withoutWord.filter(
      (match) => !idsEqual(match.defId, definitionId)
    );

    setMatches([
      ...withoutTarget,
      {
        wordId,
        defId: definitionId,
      },
    ]);

    setSelectedWord(null);
  }

  function handleDragStart(event, word) {
    if (answerLocked || !word) {
      return;
    }

    try {
      event.dataTransfer.setData("text/plain", String(word.id));
      event.dataTransfer.effectAllowed = "move";
    } catch {}

    setSelectedWord(word);
  }

  function handleDrop(event, definitionId) {
    if (answerLocked || isTouchDevice) {
      return;
    }

    event.preventDefault();

    const raw = event.dataTransfer.getData("text/plain");
    const wordId = raw || selectedWord?.id;

    if (!wordId) {
      return;
    }

    placeWordIntoDefinition(wordId, definitionId);
  }

  const allFilled =
    definitions.length > 0 &&
    definitions.every((definition) =>
      matches.some((match) =>
        idsEqual(match.defId, definition.id)
      )
    );

  const allCorrect = useMemo(() => {
    if (!allFilled) {
      return false;
    }

    return definitions.every((definition) => {
      const match = matches.find((item) =>
        idsEqual(item.defId, definition.id)
      );

      const word = getWordById(match?.wordId);

      return effectiveValidatePair(word, definition);
    });
  }, [
    allFilled,
    definitions,
    matches,
    getWordById,
    effectiveValidatePair,
  ]);

  const visibleWords = shuffledWords.filter(
    (word) => !isWordMatched(word.id)
  );

  const resetDragAnswers = () => {
    if (answerLocked) {
      return;
    }

    setMatches([]);
    setSelectedWord(null);
    setShuffledWords(shuffleWords(words));
  };

  const handleSubmit = async () => {
    if (verifying || !allFilled) {
      return;
    }

    /*
     * Previously completed question.
     * Show correct feedback, then continue normally.
     */
    if (answerLocked) {
      setFeedbackState("correct");
      return;
    }

    if (secureMode) {
      setVerifying(true);
      setVerifyError("");
      try {
        // Physical word IDs have a generated prefix/suffix. Resolve the original Sanity answer _key.
        const placements = {};
        for (const match of matches) {
          const word = getWordById(match.wordId);
          const definition = getDefinitionById(match.defId);
          const answerKey = word?.answerKey;
          const boxKey = definition?.boxKey;
          if (!answerKey || !boxKey) throw new Error("Invalid drag answer mapping.");
          placements[answerKey] = boxKey;
        }
        const result = await verifyKnowledgeCheck({ ...secureAssessment, answer: { placements } });
        if (result.correct) {
          setAnswerLocked(true);
          setFeedbackState("correct");
        } else {
          incrementKnowledgeCheckWrongAttempt(storageContext);
          setFeedbackState("incorrect");
        }
      } catch (error) {
        setVerifyError(error?.message || "Could not verify the answer. Please try again.");
      } finally {
        setVerifying(false);
      }
      return;
    }

    if (allCorrect) {
      saveCorrectDragAnswer(
        storageContext,
        matches
      );

      setAnswerLocked(true);
      setFeedbackState("correct");

      return;
    }

    incrementKnowledgeCheckWrongAttempt(storageContext);
    setFeedbackState("incorrect");
  };

  /*
   * Incorrect feedback retry.
   *
   * Important:
   * - Drag answers still reset.
   * - Drag answers still reshuffle.
   * - Then, if RenderSanityStep supplied onIncorrectRetry,
   *   the learner is sent to the related video.
   */
  const handleRetry = () => {
    resetDragAnswers();

    setFeedbackState("answering");

    if (onIncorrectRetry) {
      onIncorrectRetry();
    }
  };

  const handleContinue = () => {
    setFeedbackState("answering");
    onNext?.();
  };

  const handleBack = () => {
    if (onBack) {
      onBack();
      return;
    }

    if (backTo) {
      navigate(backTo);
      return;
    }

    navigate(-1);
  };

  if (
    feedbackState === "correct" ||
    feedbackState === "incorrect"
  ) {
    return (
      <KnowledgeCheckFeedback
        status={feedbackState}
        correctFeedbackText={correctFeedbackText}
        incorrectFeedbackText={incorrectFeedbackText}
        incorrectSupportText={incorrectSupportText}
        onContinue={handleContinue}
        onRetry={handleRetry}
        progressStep={progressStep}
        progressTotal={progressTotal}
      />
    );
  }

  const displayScenario = scenarioText || title || "";

  const displayPrompt = removeRepeatedScenarioFromPrompt({
    scenarioText: displayScenario,
    prompt,
  });

  return (
    <LessonShell
      progressStep={progressStep}
      progressTotal={progressTotal}
      showBack
      backLabel={backLabel}
      onBack={handleBack}
      showNext
      nextLabel={submitLabel || nextLabel}
      onNext={handleSubmit}
      nextDisabled={!allFilled || verifying}
      contentClassName="pt-8"
    >
      <p className="text-center text-[20px] font-extrabold uppercase text-[#38B6E8]">
        Knowledge Check
      </p>

      {verifyError && (
        <p role="alert" className="mx-auto mt-4 max-w-xl text-center text-sm text-red-700">
          {verifyError}
        </p>
      )}
      {verifying && (
        <p role="status" className="mt-3 text-center text-sm">Checking your answer...</p>
      )}

      <section
        className={[
          "mx-auto",
          "mt-10",
          "grid",
          "w-full",
          "max-w-[580px]",
          "grid-cols-[34%_1fr]",
          "items-center",
          "gap-4",
          "sm:mt-12",
          "sm:grid-cols-[190px_1fr]",
          "sm:gap-7",
        ].join(" ")}
      >
        {!!characterImageSrc && (
          <img
            src={characterImageSrc}
            alt={characterImageAlt}
            className="mx-auto h-auto max-h-[220px] w-full max-w-[150px] object-contain sm:max-h-[250px] sm:max-w-[190px]"
            loading="eager"
            decoding="async"
            draggable="false"
          />
        )}

        <div className="pb-3">
          {!!displayScenario && (
            <p className="text-[17px] font-medium leading-[1.45] text-[#171717]">
              {displayScenario}
            </p>
          )}

          {!!displayPrompt && (
            <p className="mt-6 text-[18px] font-extrabold leading-[1.35] text-[#111111]">
              {displayPrompt}
            </p>
          )}
        </div>
      </section>

      {showHelper && (helperMain || helperTip) && (
        <section className="mx-auto mt-7 w-full max-w-[590px] rounded-[18px] border-2 border-[#D7D7D7] bg-white px-5 py-4">
          {!!helperMain && (
            <p className="text-center text-[13px] font-bold text-[#757575]">
              {conceptIcon ? `${conceptIcon} ` : ""}
              {helperMain}
            </p>
          )}

          {!!helperTip && (
            <p className="mt-2 text-center text-[12px] font-medium text-[#9A9A9A]">
              {helperTip}
            </p>
          )}
        </section>
      )}

      {!answerLocked && visibleWords.length > 0 && (
        <section className="mx-auto mt-10 w-full max-w-[590px]">
          <div className="flex flex-wrap justify-center gap-3">
            {visibleWords.map((word, index) => {
              const selected = idsEqual(selectedWord?.id, word.id);

              return (
                <button
                  key={`${word.id}-${index}`}
                  type="button"
                  draggable={!isTouchDevice}
                  onDragStart={(event) => handleDragStart(event, word)}
                  onClick={() => toggleSelectedWord(word)}
                  className={[
                    "rounded-[14px]",
                    "border-2",
                    "px-5",
                    "py-3",
                    "text-[14px]",
                    "font-bold",
                    "transition-all",
                    selected
                      ? "border-[#FF914D] bg-[#FF914D] text-white"
                      : "border-[#FF914D] bg-white text-[#FF914D]",
                  ].join(" ")}
                >
                  {word.text}
                </button>
              );
            })}
          </div>

          {!!selectedWord && (
            <p className="mt-3 text-center text-xs font-medium text-[#FF914D]">
              Tap the box where you want to place this answer.
            </p>
          )}
        </section>
      )}

      <section className="mx-auto mt-8 w-full max-w-[590px] space-y-4">
        {definitions.map((definition, index) => {
          const match = getMatchForDefinition(definition.id);
          const matchedWord = match ? getWordById(match.wordId) : null;

          return (
            <div
              key={`${definition.id}-${index}`}
              onDragOver={(event) => {
                if (!answerLocked && !isTouchDevice) {
                  event.preventDefault();
                }
              }}
              onDrop={(event) => handleDrop(event, definition.id)}
              onClick={() => {
                if (selectedWord && !answerLocked) {
                  placeWordIntoDefinition(selectedWord.id, definition.id);
                }
              }}
              className="rounded-[18px] border-[3px] border-[#D7D7D7] bg-white px-5 py-4"
            >
              <p className="text-[15px] font-semibold leading-[1.45] text-[#151515]">
                {definition.text}
              </p>

              <div
                className={[
                  "mt-4",
                  "flex",
                  "min-h-[52px]",
                  "items-center",
                  "justify-center",
                  "rounded-[13px]",
                  "border-2",
                  "px-4",
                  matchedWord
                    ? "border-[#FF914D] bg-[#FFF4EC] text-[#FF914D]"
                    : "border-dashed border-[#D7D7D7] text-[#AAAAAA]",
                ].join(" ")}
              >
                <p className="text-[14px] font-bold">
                  {matchedWord
                    ? matchedWord.text
                    : selectedWord
                    ? "Tap to place"
                    : "Place answer here"}
                </p>
              </div>
            </div>
          );
        })}
      </section>

      {!answerLocked && matches.length > 0 && (
        <div className="mx-auto mt-6 flex w-full max-w-[590px] justify-center">
          <button
            type="button"
            onClick={resetDragAnswers}
            className={[
              "flex",
              "h-[46px]",
              "min-w-[180px]",
              "items-center",
              "justify-center",
              "rounded-[12px]",
              "border-2",
              "border-[#FF914D]",
              "bg-white",
              "px-5",
              "text-[14px]",
              "font-extrabold",
              "text-[#FF914D]",
              "shadow-[0_4px_0_#cc4e00]",
              "transition",
              "active:translate-y-[2px]",
              "active:shadow-[0_2px_0_#cc4e00]",
            ].join(" ")}
          >
            Reset answers
          </button>
        </div>
      )}
    </LessonShell>
  );
}